/**
 * v2 Card — minimal card shell matching prototype's widgets.jsx <Card>.
 *
 * Title row optional, with optional right-aligned action slot. Wraps the
 * existing @ddmarc/ui Card for visual consistency with the v2 token scope.
 */

import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

interface CardV2Props extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  bodyClassName?: string;
}

export function CardV2({
  title,
  subtitle,
  action,
  bodyClassName,
  className,
  children,
  ...rest
}: CardV2Props) {
  const hasHeader = title !== undefined || subtitle !== undefined || action !== undefined;
  return (
    <div
      className={cn(
        "rounded-lg border border-border bg-card text-card-foreground shadow-[var(--v2-shadow-sm)]",
        className
      )}
      {...rest}
    >
      {hasHeader && (
        <div className="flex items-start justify-between gap-3 px-4 pt-3.5 pb-2">
          <div className="min-w-0">
            {title !== undefined && (
              <div className="text-[14px] font-semibold leading-tight text-foreground">
                {title}
              </div>
            )}
            {subtitle !== undefined && (
              <div className="text-[11.5px] text-muted-foreground mt-0.5">{subtitle}</div>
            )}
          </div>
          {action !== undefined && <div className="flex shrink-0 items-center gap-2">{action}</div>}
        </div>
      )}
      <div className={cn(hasHeader ? "px-4 pb-4" : "p-4", bodyClassName)}>{children}</div>
    </div>
  );
}
